import moment from "moment";
import { TableData } from "../../../context/tablesContextProvider";

type SortType = "date" | "string" | "number";

interface SortOptions {
  key: string;
  subKey?: string;
  type?: SortType;
  sortAscending: boolean;
}

/**
 * Computes the rows to render for the current page, optionally applying an
 * in-memory sort based on `tableAttributes`. Also drives `handlePageChange`
 * with the resolved start/end indices so downstream cell data stays in sync.
 *
 * @param searchResults - The full working dataset (post-search)
 * @param tableAttributes - Per-table context entry containing sort and pagination state
 * @param itemsPerPage - Page size used to derive a default end index when context is empty
 * @param handlePageChange - Callback invoked with the resolved page window and the
 *   (possibly sorted) data slice
 * @param shouldDisableUiSorting - When true, skip the in-memory sort and render rows
 *   in the order provided by the parent (server-side sort scenario)
 * @returns The (possibly sorted) data array used for rendering
 */
const updateTableDataToRender = (
  searchResults: any[],
  tableAttributes: TableData[string],
  itemsPerPage: number,
  handlePageChange: (startIndex: number, endIndex: number, data: any[]) => void,
  shouldDisableUiSorting: boolean,
): any[] => {
  if (searchResults.length === 0) return [];

  const sortByKey = tableAttributes?.sortByKey;
  const sortBySubKey = tableAttributes?.sortBySubKey;
  const sortType = tableAttributes?.sortType;

  const getIndices = (): { startIndex: number; endIndex: number } => {
    const startIndex = Number(tableAttributes?.startIndex) || 0;
    const endIndex = Number(tableAttributes?.endIndex) || itemsPerPage - 1;
    return { startIndex, endIndex };
  };

  const updateTableData = (startIndex: number, endIndex: number): any[] => {
    let updatedTableData = searchResults;
    if ((sortByKey || sortBySubKey) && !shouldDisableUiSorting) {
      updatedTableData = sortTableData(updatedTableData, {
        key: sortByKey as string,
        subKey: sortBySubKey ? (sortBySubKey as string) : undefined,
        type: sortType,
        sortAscending: Boolean(tableAttributes?.sortAscending) ?? true,
      });
    }
    handlePageChange(startIndex, endIndex, updatedTableData);
    return updatedTableData;
  };

  const { startIndex, endIndex } = getIndices();

  return startIndex < searchResults.length
    ? updateTableData(startIndex, endIndex)
    : updateTableData(0, itemsPerPage - 1);
};

export function sortTableData<T extends Record<string, any>>(
  data: T[],
  { key, subKey, type = "string", sortAscending }: SortOptions,
): T[] {
  const getValue = (item: T) => (subKey ? item[key]?.[subKey] : item[key]);

  const parseMDYString = (dateStr: string): Date => {
    const [month, day, year] = dateStr.split("/").map(Number);
    return new Date(year, month - 1, day);
  };

  // Date sorting - return early
  if (type === "date") {
    return [...data].sort((a, b) => {
      const valA = getValue(a);
      const valB = getValue(b);
      const dateA = parseMDYString(moment(valA).format("MM/DD/YYYY"));
      const dateB = parseMDYString(moment(valB).format("MM/DD/YYYY"));
      return sortAscending
        ? dateA.getTime() - dateB.getTime()
        : dateB.getTime() - dateA.getTime();
    });
  }

  // Numeric/String sorting
  const isNumeric = (val: any): boolean => /^-?\d+(\.\d+)?$/.test(val);

  return [...data].sort((a, b) => {
    const valA = getValue(a);
    const valB = getValue(b);

    if (isNumeric(valA) && isNumeric(valB)) {
      return sortAscending
        ? Number(valA) - Number(valB)
        : Number(valB) - Number(valA);
    }

    return sortAscending
      ? String(valA).localeCompare(String(valB))
      : String(valB).localeCompare(String(valA));
  });
}

export default updateTableDataToRender;
